import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
} from '@mui/material';
import {
  ArrowBack,
  Share,
  Star,
  ExpandMore,
  Kitchen,
} from '@mui/icons-material';
import { Recipe } from '../utils/MockAPI';
import { mockGetRecipeById } from '../utils/MockAPI';
import { useAuth } from '../hooks/useAuth';
import { useRecipeRating } from '../hooks/useRecipeRating';

const RecipeCard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const {
    ratingOpen,
    rating,
    setRating,
    handleRatingSubmit,
    openRatingDialog,
    closeRatingDialog
  } = useRecipeRating(id || '', userId, () => {
    setSnackbar({ open: true, message: 'Thank you for helping to make Create A Dish better!', severity: 'success' });
  });

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await mockGetRecipeById(id);
        setRecipe(data);
        setSnackbar({ open: true, message: 'Recipe loaded successfully!', severity: 'success' });
      } catch (error) {
        console.error('Error fetching recipe:', error);
        setSnackbar({ open: true, message: 'Failed to load recipe. Please try again.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id]);

  const handleShare = async () => {
    if (!recipe) return;

    const shareData = {
      title: recipe.title,
      text: `Check out this recipe: ${recipe.title}`,
      url: window.location.href,
    };

    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setSnackbar({ open: true, message: 'Recipe shared successfully!', severity: 'success' });
      } catch (error) {
        // User cancelled sharing or error occurred
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          setSnackbar({ open: true, message: 'Failed to share recipe.', severity: 'error' });
        }
      }
    } else {
      // Fallback: Copy URL to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        setSnackbar({ open: true, message: 'Recipe link copied to clipboard!', severity: 'success' });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        // Final fallback: Show URL in alert
        alert(`Share this recipe: ${window.location.href}`);
      }
    }
  };

  const handleStartCooking = () => {
    if (id) {
      navigate(`/cook-mode/${id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!recipe) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Recipe not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', maxWidth: '1200px', mx: 'auto' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" sx={{ color: 'white' }} onClick={() => navigate('/results')} aria-label="Back to results">
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" sx={{ ml: 1, fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', cursor: 'pointer', '&:hover': { opacity: 0.8 } }} onClick={() => navigate('/results')}>
              Results
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', display: { xs: 'none', md: 'block' } }}>
            {recipe.title}
          </Typography>
          <IconButton sx={{ color: 'white' }} onClick={handleShare} aria-label="Share recipe">
            <Share />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<Star />}
            onClick={openRatingDialog}
            sx={{
              ml: 1,
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
            aria-label="Rate recipe"
          >
            Rate
          </Button>
          <Button
            variant="contained"
            startIcon={<Kitchen />}
            onClick={handleStartCooking}
            sx={{
              ml: 2,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              py: 0.5,
              px: 1.5,
            }}
            aria-label="Start cooking"
          >
            Cook Mode
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <CardMedia
          component="img"
          height="200"
          image={recipe.imageUrl}
          alt={`Image of ${recipe.title}`}
        />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {recipe.title}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {recipe.description}
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Ingredients</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1,
                }}
              >
                {recipe.ingredients.map((ingredient, index) => (
                  <Typography key={index} variant="body2" sx={{ py: 0.5 }}>
                    • {ingredient}
                  </Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Instructions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {recipe.steps.map((step, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={`${index + 1}. ${step}`} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Nutrition</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="body2">Calories: 450</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Protein: 25g</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Carbs: 50g</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Fat: 20g</Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Tips & Variations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Based on categories: {recipe.categories?.join(', ')} - Add spices for heat, use gluten-free alternatives, etc.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
      <Dialog open={ratingOpen} onClose={closeRatingDialog}>
        <DialogTitle>Rate this Recipe</DialogTitle>
        <DialogContent>
          <Rating
            value={rating}
            onChange={(_, newValue) => setRating(newValue)}
            size="large"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRatingDialog}>Cancel</Button>
          <Button onClick={handleRatingSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecipeCard;